import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Leaf, Lock, Download, RefreshCw, LogOut, Inbox, PhoneCall } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function csvCell(v) {
  let s = String(v ?? "");
  // Guard against spreadsheet formula injection.
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return `"${s.replace(/"/g, '""')}"`;
}

function toCSV(rows, columns) {
  const header = columns.map((c) => c.label).join(",");
  const body = rows
    .map((r) => columns.map((c) => csvCell(r[c.key])).join(","))
    .join("\n");
  return `${header}\n${body}`;
}

function download(filename, text) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const CONTACT_COLS = [
  { key: "created_at", label: "Date" },
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "company", label: "Company" },
  { key: "country", label: "Country" },
  { key: "product_interest", label: "Product Interest" },
  { key: "message", label: "Message" },
];

const CALLBACK_COLS = [
  { key: "created_at", label: "Date" },
  { key: "name", label: "Name" },
  { key: "phone", label: "Phone" },
  { key: "country", label: "Country" },
  { key: "product_interest", label: "Product Interest" },
  { key: "preferred_time", label: "Preferred Time" },
];

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function AdminDashboard() {
  const [token, setToken] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [callbacks, setCallbacks] = useState([]);

  const fetchAll = async (tok) => {
    setLoading(true);
    try {
      const headers = { "X-Admin-Token": tok };
      const [c, cb] = await Promise.all([
        axios.get(`${API}/contact`, { headers }),
        axios.get(`${API}/callback`, { headers }),
      ]);
      setContacts(c.data);
      setCallbacks(cb.data);
      setAuthed(true);
      return true;
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Invalid admin token.");
      } else {
        toast.error("Failed to load submissions.");
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  const login = async (e) => {
    e.preventDefault();
    if (!token) return;
    await fetchAll(token);
  };

  const logout = () => {
    setAuthed(false);
    setToken("");
    setContacts([]);
    setCallbacks([]);
  };

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#1A2118] px-6">
        <form
          data-testid="admin-login-form"
          onSubmit={login}
          className="w-full max-w-md rounded-[2px] border border-[#F4F1EB]/15 bg-[#F4F1EB]/[0.04] p-8"
        >
          <div className="mb-8 flex items-center gap-2.5">
            <Leaf strokeWidth={1.5} className="h-6 w-6 text-[#B85D43]" />
            <span className="font-serif-display text-2xl font-bold text-[#B85D43]">
              Herb Labs
            </span>
          </div>
          <h1 className="font-serif-display text-3xl font-light text-[#F4F1EB]">
            Submissions Dashboard
          </h1>
          <p className="mt-2 text-sm text-[#F4F1EB]/60">
            Enter your admin token to view inquiries & callback requests.
          </p>
          <div className="mt-6 space-y-2">
            <Label className="overline text-[#F4F1EB]/60">Admin Token</Label>
            <Input
              data-testid="admin-token-input"
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Enter admin token"
              className="h-12 rounded-[2px] border-[#3A4536]/30 bg-[#EAE5D9] text-[#1A2118] placeholder:text-[#3A4536]/70 focus-visible:ring-1 focus-visible:ring-[#B85D43]"
            />
          </div>
          <button
            data-testid="admin-login-btn"
            type="submit"
            disabled={loading}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[#B85D43] px-8 py-4 text-sm text-[#F4F1EB] transition-colors hover:bg-[#D4A373] hover:text-[#1A2118] disabled:opacity-60"
          >
            <Lock strokeWidth={1.5} className="h-4 w-4" />
            {loading ? "Verifying..." : "Unlock Dashboard"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F4F1EB] px-6 py-10 md:px-10">
      <div className="mx-auto max-w-[1400px]">
        <div className="mb-10 flex flex-col justify-between gap-4 md:flex-row md:items-center">
          <div className="flex items-center gap-2.5">
            <Leaf strokeWidth={1.5} className="h-6 w-6 text-[#B85D43]" />
            <span className="font-serif-display text-2xl font-bold text-[#B85D43]">Herb Labs</span>
            <span className="ml-2 overline text-[#3A4536]">Submissions</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-btn"
              onClick={() => fetchAll(token)}
              className="flex items-center gap-2 rounded-full border border-[#1A2118] px-5 py-2.5 text-sm text-[#1A2118] transition-colors hover:bg-[#1A2118] hover:text-[#F4F1EB]"
            >
              <RefreshCw strokeWidth={1.5} className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="flex items-center gap-2 rounded-full bg-[#1A2118] px-5 py-2.5 text-sm text-[#F4F1EB] transition-colors hover:bg-[#B85D43]"
            >
              <LogOut strokeWidth={1.5} className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>

        <div className="mb-8 grid grid-cols-2 gap-4 md:max-w-md">
          <StatCard icon={Inbox} label="Inquiries" value={contacts.length} />
          <StatCard icon={PhoneCall} label="Callbacks" value={callbacks.length} />
        </div>

        <Tabs defaultValue="contacts">
          <div className="mb-6 flex flex-col justify-between gap-4 md:flex-row md:items-center">
            <TabsList className="bg-[#EAE5D9]">
              <TabsTrigger value="contacts" data-testid="tab-contacts">Contact Inquiries</TabsTrigger>
              <TabsTrigger value="callbacks" data-testid="tab-callbacks">Callback Requests</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="contacts">
            <ExportBar
              testid="export-contacts-btn"
              onExport={() =>
                download(`herblabs-inquiries-${Date.now()}.csv`, toCSV(contacts, CONTACT_COLS))
              }
              count={contacts.length}
            />
            <DataTable rows={contacts} columns={CONTACT_COLS} empty="No inquiries yet." />
          </TabsContent>

          <TabsContent value="callbacks">
            <ExportBar
              testid="export-callbacks-btn"
              onExport={() =>
                download(`herblabs-callbacks-${Date.now()}.csv`, toCSV(callbacks, CALLBACK_COLS))
              }
              count={callbacks.length}
            />
            <DataTable rows={callbacks} columns={CALLBACK_COLS} empty="No callback requests yet." />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-[2px] border border-[#3A4536]/15 bg-white p-5">
      <Icon strokeWidth={1.5} className="h-5 w-5 text-[#4A5D4E]" />
      <div className="mt-3 font-serif-display text-4xl font-light text-[#1A2118]">{value}</div>
      <div className="overline mt-1 text-[#3A4536]">{label}</div>
    </div>
  );
}

function ExportBar({ onExport, count, testid }) {
  return (
    <div className="mb-4 flex items-center justify-between">
      <span className="text-sm text-[#3A4536]">{count} record{count === 1 ? "" : "s"}</span>
      <button
        data-testid={testid}
        onClick={onExport}
        disabled={count === 0}
        className="flex items-center gap-2 rounded-full bg-[#4A5D4E] px-5 py-2.5 text-sm text-[#F4F1EB] transition-colors hover:bg-[#1A2118] disabled:opacity-50"
      >
        <Download strokeWidth={1.5} className="h-4 w-4" />
        Export CSV
      </button>
    </div>
  );
}

function DataTable({ rows, columns, empty }) {
  if (!rows.length) {
    return (
      <div className="rounded-[2px] border border-dashed border-[#3A4536]/25 bg-white p-16 text-center text-sm text-[#3A4536]">
        {empty}
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-[2px] border border-[#3A4536]/15 bg-white">
      <table className="w-full min-w-[720px] text-left text-sm">
        <thead>
          <tr className="bg-[#1A2118]">
            {columns.map((c) => (
              <th key={c.key} className="overline whitespace-nowrap px-4 py-3 text-[#D4A373]">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i} className="border-t border-[#3A4536]/10 align-top hover:bg-[#EAE5D9]/50">
              {columns.map((c) => (
                <td key={c.key} className="px-4 py-3 text-[#1A2118]">
                  {c.key === "created_at" ? fmtDate(r[c.key]) : (r[c.key] || "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
